import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import AxiosConfig from '../../../config/AxiosConfig';
import TicketsTableModal from './TicketsStatsModal.jsx';
import TicketsStatsTable from './TicketsStatsTable.jsx';
import { getColor } from './utils';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxHeight: '80%',
  overflow: 'auto',
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: (theme) => theme.shadows[5],
  p: 4,
};

const TicketsStats = ({ sensitiveDataAllowedToUser }) => {
  const navigate = useNavigate();
  const users = useSelector((state) => state?.static?.users);
  const services = useSelector((state) => state?.static?.services);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [filteredService, setFilteredService] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMonthDetails, setSelectedMonthDetails] = useState(null);
  const [period, setPeriod] = useState(6);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedOperatorId, setSelectedOperatorId] = useState('');

  const fetchStats = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('months', period);
      if (filteredService) {
        params.append('serviceCode', filteredService);
      }
      if (selectedOperatorId && sensitiveDataAllowedToUser) {
        params.append('operatorId', selectedOperatorId);
      }

      await AxiosConfig.create(
        'get',
        `/stats/serviceTicketMetrics?${params.toString()}`
      )
        .call()
        .exit((res) => {
          setMonthlyStats(res?.data?.monthlyStats || []);
        });
    } catch (error) {
      console.error(`Error fetching stats:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [period, filteredService, selectedOperatorId, sensitiveDataAllowedToUser]);

  const handleTicketClick = (ticket) => {
    navigate(`/project/${ticket.projectId}/tickets/${ticket.ticketId}`);
    setIsDetailsOpen(false);
  };

  const filteredStats = monthlyStats.filter((stat) => {
    return filteredService ? stat.serviceCode === filteredService : true;
  });

  // Utiliser la liste des services depuis Redux pour les options du filtre
  const serviceOptions = services || [];

  const handleBarClick = (event, _index) => {
    if (event?.activePayload && event.activePayload.length > 0) {
      const clickedData = event.activePayload[0].payload;
      const details = monthlyStats.find(
        (stat) =>
          stat.month === clickedData.month &&
          stat.serviceCode === clickedData.service
      );
      setSelectedMonthDetails(details);

      const filteredUnprocessedTickets =
        details?.unprocessedTickets?.filter(
          (ticket) =>
            !selectedOperatorId || ticket.operatorId === selectedOperatorId
        ) || [];

      const filteredOpenedTickets =
        details?.openedTickets
          ?.filter(
            (openedTicket) =>
              !details?.unprocessedTickets?.some(
                (unprocessedTicket) =>
                  unprocessedTicket.ticketId === openedTicket.ticketId
              )
          )
          ?.filter(
            (openedTicket) =>
              !selectedOperatorId ||
              openedTicket.operatorId === selectedOperatorId
          ) || [];

      if (
        filteredUnprocessedTickets.length > 0 ||
        filteredOpenedTickets.length > 0
      ) {
        setIsDetailsOpen(true);
      }
    }
  };

  const handleCloseModal = () => {
    setIsDetailsOpen(false);
    setSelectedMonthDetails(null);
  };

  const handlePeriodChange = (event) => {
    setPeriod(event.target.value);
  };

  const handleOperatorChange = (event) => {
    setSelectedOperatorId(event.target.value);
  };

  const lineChartData = (monthlyStats) => {
    const totalByMonth = {};
    monthlyStats.forEach((stat) => {
      totalByMonth[stat.month] =
        (totalByMonth[stat.month] || 0) + stat.totalTickets;
    });
    const lineDataArray = Object.keys(totalByMonth).map((month) => ({
      month: month,
      Total: totalByMonth[month],
    }));
    if (filteredService) {
      const serviceData = monthlyStats.filter(
        (stat) => stat.serviceCode === filteredService
      );
      return lineDataArray.map((totalItem) => ({
        ...totalItem,
        [filteredService]: serviceData.find(
          (item) => item.month === totalItem.month
        )?.totalTickets,
      }));
    } else {
      return Array.from(new Set(monthlyStats.map((stat) => stat.month))).map(
        (month) => {
          const monthData = { month, Total: totalByMonth[month] };
          monthlyStats.forEach((stat) => {
            if (stat.month === month) {
              monthData[stat.serviceCode] = stat.totalTickets;
            }
          });
          return monthData;
        }
      );
    }
  };

  const lineData = lineChartData(monthlyStats);
  const lineKeys = filteredService
    ? ['Total', filteredService]
    : Object.keys(lineData[0] || {}).filter((key) => key !== 'month');
  const lineColors = { Total: '#8884d8' };

  // Générer les couleurs pour les services depuis Redux
  serviceOptions.forEach((service, index) => {
    lineColors[service.code] = `hsl(${index * 50}, 60%, 70%)`;
  });

  const CustomTooltipTotalWithMarkers = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 2 }}>
          <Typography>{label}</Typography>
          <Typography color="text.primary">{`Service: ${data?.service}`}</Typography>
          <Typography color="text.primary">{`Total Créés: ${data?.total}`}</Typography>
          {payload.find((item) => item.dataKey === 'closed') && (
            <Typography
              color={getColor(
                payload.find((item) => item.dataKey === 'closed')?.value,
                'closed'
              )}
            >
              {`Fermés: ${payload.find((item) => item.dataKey === 'closed')?.value}`}
            </Typography>
          )}
          {payload.find((item) => item.dataKey === 'opened') && (
            <Typography
              color={getColor(
                payload.find((item) => item.dataKey === 'opened')?.value,
                'opened'
              )}
            >
              {`Traités: ${payload.find((item) => item.dataKey === 'opened')?.value}`}
            </Typography>
          )}
          {payload.find((item) => item.dataKey === 'toTreat') && (
            <Typography
              color={getColor(
                payload.find((item) => item.dataKey === 'toTreat')?.value,
                'toTreat'
              )}
            >
              {`À traiter: ${payload.find((item) => item.dataKey === 'toTreat')?.value}`}
            </Typography>
          )}
          {data?.totalProcessedInPeriod !== undefined && (
            <Typography
              color={getColor(
                data.totalProcessedInPeriod,
                'totalProcessedInPeriod'
              )}
            >
              {`Total Traités sur la Période: ${data.totalProcessedInPeriod}`}
            </Typography>
          )}
        </Paper>
      );
    }
    return null;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Modal
        open={isDetailsOpen}
        onClose={handleCloseModal}
        aria-labelledby="ticket-details-modal-title"
        aria-describedby="ticket-details-modal-description"
      >
        <Box sx={style}>
          <Typography
            id="ticket-details-modal-title"
            variant="h6"
            component="h2"
            sx={{ mb: 2 }}
          >
            Détails pour {selectedMonthDetails?.serviceCode} en{' '}
            {selectedMonthDetails?.month}
            <IconButton
              aria-label="fermer"
              onClick={handleCloseModal}
              sx={{ position: 'absolute', top: 8, right: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </Typography>

          {selectedMonthDetails?.unprocessedTickets?.length > 0 && (
            <TicketsTableModal
              title="Tickets à traiter"
              tickets={selectedMonthDetails.unprocessedTickets}
              handleTicketClick={handleTicketClick}
            />
          )}

          {selectedMonthDetails?.openedTickets?.length > 0 && (
            <TicketsTableModal
              title="Tickets ouverts"
              tickets={selectedMonthDetails.openedTickets.filter(
                (openedTicket) =>
                  !selectedMonthDetails.unprocessedTickets.some(
                    (unprocessedTicket) =>
                      unprocessedTicket.ticketId === openedTicket.ticketId
                  )
              )}
              handleTicketClick={handleTicketClick}
            />
          )}
        </Box>
      </Modal>

      <Typography variant="h5" gutterBottom>
        Statistiques des Tickets
      </Typography>

      <Grid container spacing={2} alignItems="center" mb={2}>
        <Grid>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="period-select-label">Période</InputLabel>
            <Select
              labelId="period-select-label"
              value={period}
              label="Période"
              onChange={handlePeriodChange}
            >
              <MenuItem value={3}>Derniers 3 mois</MenuItem>
              <MenuItem value={6}>Derniers 6 mois</MenuItem>
              <MenuItem value={12}>Dernière année</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="service-filter-label">
              Filtrer par service
            </InputLabel>
            <Select
              labelId="service-filter-label"
              value={filteredService}
              label="Filtrer par service"
              onChange={(e) => setFilteredService(e.target.value)}
            >
              <MenuItem value="">Tous</MenuItem>
              {serviceOptions
                .filter((service) =>
                  ['SCO', 'SLO', 'SAV', 'MP'].includes(service.code)
                )
                .map(
                  (
                    service // Utiliser serviceOptions ici
                  ) => (
                    <MenuItem key={service._id} value={service.code}>
                      {service.code}
                    </MenuItem>
                  )
                )}
            </Select>
          </FormControl>
        </Grid>
        <Grid>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="operator-filter-label">
              Filtrer par opérateur
            </InputLabel>
            <Select
              labelId="operator-filter-label"
              value={selectedOperatorId}
              label="Filtrer par opérateur"
              onChange={handleOperatorChange}
              disabled={!sensitiveDataAllowedToUser}
            >
              <MenuItem value="">Tous</MenuItem>
              {users
                ?.filter((user) => user.isActive === true)
                ?.map((user) => (
                  <MenuItem key={user._id} value={user._id}>
                    {user.firstName} {user.lastName}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {isLoading ? (
        <CircularProgress />
      ) : filteredStats.length === 0 ? (
        <Typography>Aucune donnée disponible</Typography>
      ) : (
        <Grid container spacing={4}>
          {/* Première ligne : Deux graphiques (1/2 chacun sur écrans moyens et grands) */}
          <Grid size={6}>
            <Paper sx={{ p: 2, height: 400 }}>
              <Typography variant="h6" mb={2}>
                Tendances Mensuelles
                {filteredService && ` (${filteredService} vs Total)`}
                {!filteredService && ' (par Service)'}
              </Typography>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={lineData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {lineKeys.map((key) => (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      stroke={lineColors[key] || '#000'}
                      name={key}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          <Grid size={6}>
            <Paper sx={{ p: 2, height: 400 }}>
              <Typography variant="h6" mb={2}>
                Comparaison par Mois et Service
              </Typography>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={filteredStats.map((stat) => ({
                    month: stat.month,
                    service: stat.serviceCode,
                    closed: stat.closed,
                    opened: stat.opened,
                    toTreat: stat.toTreat,
                    total: stat.totalTickets,
                    totalProcessedInPeriod: stat.totalProcessedInPeriod,
                  }))}
                  margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
                  onClick={handleBarClick}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip content={<CustomTooltipTotalWithMarkers />} />
                  <Legend />
                  <Bar
                    dataKey="closed"
                    fill="#4caf50"
                    name="Fermés"
                    stackId="a"
                  />
                  <Bar
                    dataKey="opened"
                    fill="#ff9800"
                    name="Traités"
                    stackId="a"
                  />
                  <Bar
                    dataKey="toTreat"
                    fill="#f44336"
                    name="À traiter"
                    stackId="a"
                  />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Deuxième ligne : Tableau de comparaison par mois et service (pleine largeur) */}
          <TicketsStatsTable filteredStats={filteredStats} />
        </Grid>
      )}
    </Box>
  );
};

export default TicketsStats;
